// Import React hooks for managing component state and optimizing performance
import { useState, useMemo } from 'react';
// Import icon components from Lucide React icon library for the mobile interface
import { MoreVertical, Folder, Target, CheckCircle, Calendar, ChevronDown, ChevronUp, FolderOpen, Check } from 'lucide-react';
// Import TypeScript type definitions for data structures
import { Project, Matter, Goal, Task } from '../../lib/db';

// Define the props this mobile project card component expects
// Similar to desktop version but optimized for touch interfaces
interface ProjectCardMobileProps {
  project: Project;    // The project data to display in this mobile card
  matters: Matter[];   // Array of all matters (some linked to this project)
  goals: Goal[];      // Array of all goals/deliverables (some linked to this project)
  tasks?: Task[];     // Optional tasks array for calculating completion statistics
  // Callback functions for user actions:
  onEdit: (project: Project) => void;           // Called when user taps edit option
  onDelete: (project: Project) => void;         // Called when user confirms deletion
  onMarkComplete?: (project: Project) => void;  // Optional: Mark project as complete
  onMarkIncomplete?: (project: Project) => void; // Optional: Mark project as incomplete
}

// ProjectCardMobile displays project information optimized for mobile devices
// Features touch-friendly interface with dropdown menus and expandable sections
export function ProjectCardMobile({
  project,
  matters,
  goals,
  tasks,
  onEdit,
  onDelete,
  onMarkComplete,
  onMarkIncomplete,
}: ProjectCardMobileProps) {
  // State to control the visibility of the action menu (three dots menu)
  const [showMenu, setShowMenu] = useState(false);
  // State to track whether the card details are expanded or collapsed
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Filter and organize data related to this specific project
  // Same logic as desktop version but crucial for mobile display calculations
  const associatedMatters = matters.filter(matter => matter.project_id === project.id);
  const directGoals = goals.filter(goal => goal.project_id === project.id);
  const mattersGoals = goals.filter(goal => 
    goal.matter_id && associatedMatters.some(matter => matter.id === goal.matter_id)
  );
  const allProjectGoals = [...directGoals, ...mattersGoals];
  const uniqueGoals = Array.from(new Map(allProjectGoals.map(g => [g.id, g])).values());
  
  const statistics = useMemo(() => {
    if (!tasks) return { totalTasks: 0, completedTasks: 0, allDeliverablesComplete: false };
    
    const goalIds = uniqueGoals.map(g => g.id);
    const projectTasks = tasks.filter(task => task.goal_id && goalIds.includes(task.goal_id));
    const completedTasks = projectTasks.filter(task => task.status === 'Complete').length;
    
    // Check if all deliverables are complete (have all their tasks completed)
    const allDeliverablesComplete = uniqueGoals.length > 0 && uniqueGoals.every(goal => {
      const goalTasks = tasks.filter(task => task.goal_id === goal.id);
      return goalTasks.length > 0 && goalTasks.every(task => task.status === 'Complete');
    });
    
    return {
      totalTasks: projectTasks.length,
      completedTasks,
      allDeliverablesComplete,
    };
  }, [tasks, uniqueGoals]);

  // Function to handle project deletion with confirmation
  const handleDelete = () => {
    // Show browser confirmation dialog to prevent accidental deletion
    if (window.confirm(`Are you sure you want to delete "${project.title}"?`)) {
      onDelete(project); // Call the delete function provided by parent component
    }
    setShowMenu(false); // Close the menu after action (whether confirmed or not)
  };

  // Function to determine badge colors based on project status
  // Returns Tailwind CSS classes for background, text, and border colors
  const getStatusColor = () => {
    switch (project.status) {
      case 'Active':
        return 'bg-green-100 text-green-800 border-green-200'; // Light green for active projects
      case 'Completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';     // Light blue for completed projects
      case 'Archived':
        return 'bg-gray-100 text-gray-800 border-gray-200';     // Gray for archived projects
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';     // Default to gray
    }
  };

  // Function to format dates for mobile display (shorter format to save space)
  const formatDate = (date: Date | undefined): string => {
    if (!date) return ''; // Return empty string if no date provided
    // Format as "Jan 15" (month abbreviation + day) to save mobile screen space
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short',  // Short month name (Jan, Feb, etc.)
      day: 'numeric'   // Day number (1, 2, 15, etc.)
    });
  };

  // Calculate completion percentage for the progress bar
  // If no tasks, show 0%; otherwise calculate completed/total * 100
  const completionPercentage = statistics.totalTasks > 0 
    ? Math.round((statistics.completedTasks / statistics.totalTasks) * 100)
    : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center flex-1 min-w-0">
            <FolderOpen className="h-5 w-5 text-gray-400 flex-shrink-0 mr-2" />
            <h3 className="text-base font-medium text-gray-900 truncate">
              {project.title}
            </h3>
            {project.is_complete && (
              <CheckCircle className="h-4 w-4 text-green-500 ml-1 flex-shrink-0" />
            )}
          </div>
          <div className="relative ml-2">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <MoreVertical className="h-5 w-5 text-gray-400" />
            </button>
            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-8 z-20 w-48 bg-white rounded-md shadow-lg border border-gray-200">
                  <button
                    onClick={() => {
                      onEdit(project);
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Edit project
                  </button>
                  {statistics.allDeliverablesComplete && !project.is_complete && onMarkComplete && (
                    <button
                      onClick={() => {
                        onMarkComplete(project);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-gray-100"
                    >
                      Mark Complete
                    </button>
                  )}
                  {project.is_complete && onMarkIncomplete && (
                    <button
                      onClick={() => {
                        onMarkIncomplete(project);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Mark Incomplete
                    </button>
                  )}
                  <button
                    onClick={handleDelete}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    Delete project
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="mb-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor()}`}>
            {project.status}
          </span>
        </div>

        {/* Description */}
        {project.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {project.description}
          </p>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="flex items-center text-sm text-gray-600">
            <Folder className="h-4 w-4 mr-1.5 text-gray-400" />
            <span>{associatedMatters.length} matter{associatedMatters.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Target className="h-4 w-4 mr-1.5 text-gray-400" />
            <span>{uniqueGoals.length} deliverable{uniqueGoals.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Dates */}
        {(project.start_date || project.deadline) && (
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            {project.start_date && (
              <div className="flex items-center text-green-600">
                <Calendar className="h-3.5 w-3.5 mr-1" />
                <span>Start: {formatDate(project.start_date)}</span>
              </div>
            )}
            {project.deadline && (
              <div className="flex items-center text-red-600">
                <Calendar className="h-3.5 w-3.5 mr-1" />
                <span>Due: {formatDate(project.deadline)}</span>
              </div>
            )}
          </div>
        )}

        {/* Progress Bar */}
        {statistics.totalTasks > 0 && (
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-gray-600">Progress</span>
              <span className="text-xs font-medium text-gray-900">
                {statistics.completedTasks}/{statistics.totalTasks} tasks
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Expand/Collapse Button */}
        {(associatedMatters.length > 0 || directGoals.length > 0) && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-3 flex items-center justify-center w-full py-1 text-sm text-gray-600 hover:text-gray-900"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Hide details
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Show details
              </>
            )}
          </button>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (associatedMatters.length > 0 || directGoals.length > 0) && (
        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
          {associatedMatters.length > 0 && (
            <div className="mb-3">
              <h4 className="text-xs font-medium text-gray-700 mb-1">Associated Matters</h4>
              <div className="space-y-1">
                {associatedMatters.map(matter => (
                  <div key={matter.id} className="text-sm text-gray-600 pl-3">
                    • {matter.title}
                    {matter.status && matter.status !== 'Active' && (
                      <span className="ml-1 text-xs text-gray-500">({matter.status})</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {directGoals.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-700 mb-1">Direct Deliverables</h4>
              <div className="space-y-1">
                {directGoals.map(goal => (
                  <div key={goal.id} className="text-sm text-gray-600 pl-3">
                    • {goal.title}
                    {goal.category && (
                      <span className="ml-1 text-xs text-gray-500">({goal.category})</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}